import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';

import { GamePlayer } from '~/components/game/GamePlayer';
import { useHeader } from '~/contexts/HeaderContext';
import { useSubmitGameSession } from '~/hooks/useGameSession';
import { isAuthenticated } from '~/lib/auth';
import { requireAuth } from '~/lib/route-guards';
import type { PublicLandingPage } from '~/types';

export const Route = createFileRoute('/$slug/game/$episodeId')({
  beforeLoad: ({ context }: { context: Record<string, any> }) => {
    requireAuth(context.slug as string);
  },
  component: GamePage,
});

function GamePage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { episodeId } = Route.useParams();
  const submitSession = useSubmitGameSession();
  const gameStartTimeRef = useRef<string>(new Date().toISOString());
  const { setHideHeader } = useHeader();

  // Hide header immediately on game page and restore when leaving
  useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const handleGameComplete = useCallback(
    (scoreData?: { score: number; totalQuestions: number }) => {
      // Re-show the header before navigating away
      setHideHeader(false);

      // Submit game session if user is authenticated (fire-and-forget)
      if (scoreData && isAuthenticated()) {
        const pointsPerCorrectAnswer = landingPage.gameOptions.pointsPerCorrectAnswer;
        const pointsEarned = scoreData.score; // score already contains accumulated points

        submitSession.mutate({
          episodeId,
          landingPageId: landingPage.id,
          organizationId: landingPage.organizationId,
          score: scoreData.score,
          totalQuestions: scoreData.totalQuestions,
          correctAnswers: pointsPerCorrectAnswer > 0 ? Math.round(scoreData.score / pointsPerCorrectAnswer) : 0,
          pointsPerCorrectAnswer,
          pointsEarned,
          startedAt: gameStartTimeRef.current,
        });
      }

      // Navigate to score page (don't block on mutation)
      if (scoreData) {
        router.navigate({
          to: '/$slug/score',
          params: { slug },
          search: {
            score: scoreData.score,
            totalQuestions: scoreData.totalQuestions,
            pointsPerCorrectAnswer: landingPage.gameOptions.pointsPerCorrectAnswer,
          },
        });
      } else {
        router.navigate({
          to: '/$slug/offer',
          params: { slug },
        });
      }
    },
    [router, slug, landingPage, episodeId, submitSession, setHideHeader],
  );

  const handlePlayStart = useCallback(() => {
    setHideHeader(true);
  }, [setHideHeader]);

  return (
    <div className="h-[100dvh] w-screen overflow-hidden">
      <GamePlayer
        slug={slug}
        episodeId={episodeId}
        gameOptions={landingPage.gameOptions}
        onGameComplete={handleGameComplete}
        onPlayStart={handlePlayStart}
        className="h-full w-full"
      />
    </div>
  );
}
